import React from "react";
import { Box, Flex, Grid, Skeleton } from "@chakra-ui/react";
import {
  getPathPickProductCardGrid,
  PATH_PICK_PRODUCT_CARD_FRAME,
} from "@/features/pathpick/components/product/PathPickProductCard";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import { PathPickProductCardLayout } from "@/features/pathpick/types";

// 지표 행 스켈레톤 폭 — [라벨, 값] 순서로 실제 문구 길이를 흉내낸다.
// 전부 100%로 채우면 가짜 카드처럼 보여 로딩 중이라는 신호가 죽는다.
const MARKET_METRIC_SKELETON_WIDTHS: readonly [string, string][] = [
  ["64px", "72px"], // 기준가
  ["40px", "40px"], // 거래수
  ["52px", "72px"], // 예상 마진
];

// 이전 목록 개수를 모르는 첫 로드용 기본값 — 카드 그리드 최대 8열 기준 두 줄.
const DEFAULT_SKELETON_COUNT = 16;

const MetricRowSkeleton = ({
  labelWidth,
  valueWidth,
}: {
  labelWidth: string;
  valueWidth: string;
}) => (
  <Flex justifyContent={"space-between"} gap={"4px"}>
    <Skeleton height={"20px"} width={labelWidth} />
    <Skeleton height={"20px"} width={valueWidth} />
  </Flex>
);

// 마켓 1개 블록 자리 — 마켓명 + 지표 3행
const MarketBlockSkeleton = () => (
  <Flex flexDirection={"column"} gap={"4px"} flex={1} minWidth={0}>
    <Skeleton height={"20px"} width={"56px"} />
    {MARKET_METRIC_SKELETON_WIDTHS.map(([labelWidth, valueWidth], index) => (
      <MetricRowSkeleton
        key={`MarketBlockSkeletonMetric_${index}`}
        labelWidth={labelWidth}
        valueWidth={valueWidth}
      />
    ))}
  </Flex>
);

// 카드 1장 자리. 실제 카드(PathPickProductCard)의 프레임 상수를 그대로 쓰고,
// 내부 블록 높이는 각 텍스트의 lineHeight(브랜드 24px / 상품명·지표 20px)와
// 버튼 높이(AppButton 기본 28px)에 맞춰 로딩 → 실제 전환 시 높이가 튀지 않게 한다.
// 비교 블록은 보기 설정에 따라 없거나(none) 1열(side)이거나 2행(stack)이라 자리도 함께 달라진다.
const PathPickProductCardSkeleton = ({
  layout,
}: {
  layout: PathPickProductCardLayout;
}) => {
  return (
    <Flex {...PATH_PICK_PRODUCT_CARD_FRAME}>
      {/* 썸네일 — 실제 카드와 같은 padding, 정사각 이미지 */}
      <Box padding={"22px 6px"}>
        <Skeleton width={"100%"} aspectRatio={1} borderRadius={"8px"} />
      </Box>

      {/* 브랜드명 / 상품명 */}
      <Flex flexDirection={"column"} gap={"2px"}>
        <Skeleton height={"24px"} width={"60%"} />
        <Skeleton height={"20px"} width={"85%"} />
      </Flex>

      {/* SKU / 상품 상세 정보 / 예상 공급가 */}
      <Flex flexDirection={"column"} gap={"4px"}>
        <Skeleton height={"20px"} width={"56%"} />
        <Skeleton height={"20px"} width={"96px"} />
        <MetricRowSkeleton labelWidth={"64px"} valueWidth={"72px"} />
      </Flex>

      {layout !== "none" && (
        <Flex flexDirection={"column"} gap={"8px"}>
          <Flex
            flexDirection={
              layout === "side" ? { base: "column", md: "row" } : "column"
            }
            gap={"8px"}
          >
            <MarketBlockSkeleton />
            <MarketBlockSkeleton />
          </Flex>
          {/* 추천 줄 */}
          <Skeleton height={"20px"} width={"70%"} />
        </Flex>
      )}

      {/* 액션 버튼 행 — 실제 카드와 같이 marginTop auto로 바닥에 붙인다 */}
      <Flex gap={"4px"} marginTop={"auto"}>
        <Skeleton height={"28px"} width={"100%"} borderRadius={"4px"} />
        <Skeleton height={"28px"} width={"80%"} borderRadius={"4px"} />
      </Flex>
    </Flex>
  );
};

interface Props {
  // 그릴 카드 수. 전환 중에는 직전 목록 개수를 넘겨 목록 높이가 튀지 않게 한다.
  count?: number;
}

// 상품 카드 그리드가 응답을 기다리는 동안의 자리.
// 이전 결과를 딤드로 덮는 대신 스켈레톤으로 바꿔, 아직 모르는 값을 확정된 것처럼 보여주지 않는다.
// 그리드 계산과 보기 설정을 실제 목록과 공유하므로 열 수·간격이 자동으로 일치한다.
function PathPickProductCardGridSkeleton({
  count = DEFAULT_SKELETON_COUNT,
}: Props) {
  const { layout } = usePathPickProductCardView();

  return (
    <Grid {...getPathPickProductCardGrid(layout)}>
      {Array.from({ length: count }).map((_, index) => (
        <PathPickProductCardSkeleton
          key={`PathPickProductCardGridSkeleton_${index}`}
          layout={layout}
        />
      ))}
    </Grid>
  );
}

export default PathPickProductCardGridSkeleton;
